import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Facebook, Twitter, Instagram, Linkedin, Youtube, Globe, Mail, Shield, Lock, Info, BadgeCheck } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { categories } from '../../data/mock';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';

const Footer = () => {
  const { t, language, setLanguage, languages, currentLanguage } = useLanguage();

  const footerLinks = {
    discover: [
      // Use the same categories as the header - first 6 categories
      ...categories.slice(0, 6).map(cat => ({
        label: cat.name,
        href: `/discover?category=${cat.id}`
      })),
      { label: t('viewAllCategories') || 'View All Categories', href: '/discover' },
    ],
    learn: [
      { label: t('howItWorks'), href: '/how-it-works' },
      { label: t('successStories'), href: '/success-stories' },
      { label: t('blog') || 'Blog', href: '/blog' },
      { label: t('press') || 'Press', href: '/press' },
    ],
    about: [
      { label: t('aboutUs') || 'About Us', href: '/about' },
      { label: t('careers') || 'Careers', href: '/careers' },
      { label: t('becomePartner'), href: '/partners' },
      { label: t('supportUs') || 'Support Us', href: '/support-us' },
      { label: t('trustAndSafety'), href: '/trust' },
      { label: t('pricing'), href: '/pricing' },
      { label: 'Investors', href: '/investors', protected: true },
    ],
    support: [
      { label: t('helpCenter') || 'Help Center', href: '/help' },
      { label: t('contactUs') || 'Contact Us', href: '/contact' },
      { label: t('communityGuidelines') || 'Community Guidelines', href: '/guidelines' },
      { label: t('reportCampaign'), href: '/report' },
    ],
  };

  return (
    <footer className="bg-secondary text-foreground">
      {/* Trust Badges Section */}
      <div className="border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center space-y-2">
              <Shield className="w-8 h-8 text-emerald-400" />
              <span className="text-sm font-medium text-foreground">Donate Responsibly</span>
              <span className="text-xs text-muted-foreground">Give with confidence</span>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Lock className="w-8 h-8 text-emerald-400" />
              <span className="text-sm font-medium text-foreground">Secure Payments</span>
              <span className="text-xs text-muted-foreground">Protected by trusted providers</span>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <BadgeCheck className="w-8 h-8 text-teal-400" />
              <span className="text-sm font-medium text-foreground">Creator Verification</span>
              <span className="text-xs text-muted-foreground">Required for withdrawals</span>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <Globe className="w-8 h-8 text-teal-400" />
              <span className="text-sm font-medium text-foreground">Global Community</span>
              <span className="text-xs text-muted-foreground">Supporting causes worldwide</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link 
              to="/" 
              className="flex items-center space-x-2 mb-4"
              onClick={(e) => {
                // If already on home page, just scroll to top
                if (window.location.pathname === '/') {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  // Navigate and then scroll
                  setTimeout(() => window.scrollTo(0, 0), 100);
                }
              }}
            >
              <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-foreground" />
              </div>
              <span className="text-xl font-bold text-foreground">FundTheHope</span>
            </Link>
            <p className="text-sm text-muted-foreground mb-4">
              Empowering people to help people through trusted, transparent crowdfunding.
            </p>
            <div className="flex space-x-3">
              <a href="https://www.facebook.com/profile.php?id=61588000716350" target="_blank" rel="noopener noreferrer" className="p-2 bg-card rounded-lg hover:bg-emerald-600 transition-colors duration-200">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://x.com/Fundthehope01" target="_blank" rel="noopener noreferrer" className="p-2 bg-card rounded-lg hover:bg-emerald-600 transition-colors duration-200">
                <Twitter className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/fundthehope/" target="_blank" rel="noopener noreferrer" className="p-2 bg-card rounded-lg hover:bg-emerald-600 transition-colors duration-200">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://www.linkedin.com/company/fundthehope" target="_blank" rel="noopener noreferrer" className="p-2 bg-card rounded-lg hover:bg-teal-600 transition-colors duration-200">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href="https://www.youtube.com/@Fundthehope" target="_blank" rel="noopener noreferrer" className="p-2 bg-card rounded-lg hover:bg-teal-600 transition-colors duration-200">
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Discover Links - matching header categories */}
          <div>
            <h4 className="text-foreground font-semibold mb-4">Discover</h4>
            <ul className="space-y-2">
              {footerLinks.discover.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm hover:text-emerald-400 transition-colors duration-200">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Learn Links */}
          <div>
            <h4 className="text-foreground font-semibold mb-4">Learn</h4>
            <ul className="space-y-2">
              {footerLinks.learn.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm hover:text-emerald-400 transition-colors duration-200">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* About Links */}
          <div>
            <h4 className="text-foreground font-semibold mb-4">About</h4>
            <ul className="space-y-2">
              {footerLinks.about.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm hover:text-emerald-400 transition-colors duration-200 flex items-center gap-1">
                    {link.protected && <Lock className="w-3 h-3" />}
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-foreground font-semibold mb-4">Support</h4>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link to={link.href} className="text-sm hover:text-emerald-400 transition-colors duration-200">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm text-muted-foreground">
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">Terms of Service</a>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition-colors">Privacy Policy</a>
              <Link to="/cookies" className="hover:text-emerald-400 transition-colors">Cookie Policy</Link>
              <Link to="/accessibility" className="hover:text-emerald-400 transition-colors">Accessibility</Link>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-4 text-sm text-muted-foreground">
              <span>© 2025 FundTheHope. {t('allRightsReserved')}</span>
              <span className="hidden sm:inline">|</span>
              <a href="mailto:support@fundthehope.com" className="hover:text-emerald-400 transition-colors">support@fundthehope.com</a>
              
              {/* Language Selector */}
              <div className="flex items-center space-x-2">
                <Globe className="w-4 h-4" />
                <select 
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="bg-transparent border-none text-muted-foreground text-sm focus:ring-0 cursor-pointer hover:text-emerald-400 transition-colors"
                  aria-label="Select language"
                >
                  {/* Popular Languages */}
                  <optgroup label="Popular">
                    <option value="en">🇬🇧 English</option>
                    <option value="es">🇪🇸 Español</option>
                    <option value="fr">🇫🇷 Français</option>
                    <option value="de">🇩🇪 Deutsch</option>
                    <option value="zh">🇨🇳 中文</option>
                  </optgroup>
                  
                  {/* European Languages */}
                  <optgroup label="European Languages">
                    <option value="it">🇮🇹 Italiano</option>
                    <option value="pt">🇵🇹 Português</option>
                    <option value="nl">🇳🇱 Nederlands</option>
                    <option value="pl">🇵🇱 Polski</option>
                    <option value="ru">🇷🇺 Русский</option>
                    <option value="uk">🇺🇦 Українська</option>
                    <option value="sv">🇸🇪 Svenska</option>
                    <option value="no">🇳🇴 Norsk</option>
                    <option value="da">🇩🇰 Dansk</option>
                    <option value="fi">🇫🇮 Suomi</option>
                    <option value="el">🇬🇷 Ελληνικά</option>
                    <option value="cs">🇨🇿 Čeština</option>
                    <option value="ro">🇷🇴 Română</option>
                    <option value="hu">🇭🇺 Magyar</option>
                    <option value="tr">🇹🇷 Türkçe</option>
                  </optgroup>
                  
                  {/* Asian Languages */}
                  <optgroup label="Asian Languages">
                    <option value="ja">🇯🇵 日本語</option>
                    <option value="ko">🇰🇷 한국어</option>
                    <option value="hi">🇮🇳 हिन्दी</option>
                    <option value="ta">🇮🇳 தமிழ்</option>
                    <option value="th">🇹🇭 ไทย</option>
                    <option value="vi">🇻🇳 Tiếng Việt</option>
                    <option value="id">🇮🇩 Bahasa Indonesia</option>
                    <option value="ms">🇲🇾 Bahasa Melayu</option>
                    <option value="ar">🇸🇦 العربية</option>
                    <option value="bn">🇧🇩 বাংলা</option>
                    <option value="fil">🇵🇭 Filipino</option>
                  </optgroup>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
